import "../../../styles/amortisman.css";
import { Grid, html } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { uiState } from "../../state/uiState.js";
import {
	AMORTISMAN_STATUS_LABELS,
	AMORTISMAN_UYGULANMAZ_VALUE,
	addYearsDateOnly,
	fetchAmortismanDefinitions,
	formatDateOnlyTr,
	populateAmortismanSelect,
} from "./amortismanUtils.js";

const API = "/api";
const STATUS_ORDER = [
	"belirlenmedi", "yaklasiyor", "faydali_omru_doldu",
	"inceleme_bekliyor", "devam_ediyor", "uygulanmaz",
];
const FILTER_STATUSES = new Set(["actionable", "all", ...STATUS_ORDER]);
const TRACKING_SORT_FIELDS = [
	"takip_id", "demirbas_id", "barkod", "urun", "fatura_numarasi",
	"temsil_adedi", "bitis_tarihi", "kalan_gun", "durum", null,
];
const ACTION_CONFIG = {
	faydali_omur_tanimla: {
		label: "Faydal\u0131 \u00d6m\u00fcr Tan\u0131mla",
		description: "Belirlenmemi\u015f al\u0131m partisine ilk faydal\u0131 \u00f6m\u00fcr se\u00e7imini yapar.",
	},
	secimi_duzelt: {
		label: "Se\u00e7imi / Tan\u0131m\u0131 D\u00fczelt",
		description: "\u0130lk d\u00f6nemdeki hatal\u0131 tan\u0131m veya Uygulanmaz se\u00e7imini gerek\u00e7esiyle d\u00fczeltir.",
	},
	inceleme_notu: {
		label: "\u0130nceleme Notu",
		description: "Takip d\u00f6nemini de\u011fi\u015ftirmeden inceleme kayd\u0131 ekler.",
	},
	kullanima_devam: {
		label: "Kullan\u0131ma Devam",
		description: "Mevcut d\u00f6nem sonras\u0131nda yeni bir operasyonel kontrol d\u00f6nemi ba\u015flat\u0131r.",
	},
	bakim_tamir_sonrasi_devam: {
		label: "Bak\u0131m / Tamir Sonras\u0131 Devam",
		description: "Bak\u0131m veya tamir karar\u0131yla yeni bir operasyonel kontrol d\u00f6nemi ba\u015flat\u0131r.",
	},
	sure_revizyonu: {
		label: "S\u00fcreyi Revize Et",
		description: "Yeni d\u00f6nem a\u00e7madan mevcut d\u00f6nemin toplam s\u00fcresini de\u011fi\u015ftirir.",
	},
	takibi_arsivle: {
		label: "Takibi Ar\u015fivle",
		description: "Takibi geri al\u0131namayacak bi\u00e7imde aktif listeden kald\u0131r\u0131r.",
		danger: true,
	},
};
const MOVEMENT_LABELS = {
	baslangic_migrasyonu: "Takip olu\u015fturuldu",
	ilk_tanim: "\u0130lk faydal\u0131 \u00f6m\u00fcr tan\u0131mland\u0131",
	tanim_guncelleme: "Faydal\u0131 \u00f6m\u00fcr tan\u0131m\u0131 d\u00fczeltildi",
	inceleme_notu: "\u0130nceleme notu eklendi",
	kullanima_devam: "Kullan\u0131ma devam karar\u0131",
	bakim_tamir_sonrasi_devam: "Bak\u0131m / tamir sonras\u0131 devam karar\u0131",
	sure_revizyonu: "D\u00f6nem s\u00fcresi revize edildi",
	takip_arsivleme: "Takip ar\u015fivlendi",
};

let trackingGrid = null;
let didBind = false;
let definitions = [];
let wizardController = null;
let overviewController = null;
let historyController = null;
let wizardRequestId = 0;
let overviewRequestId = 0;
let historyRequestId = 0;
let wizardSubmitting = false;
let wizardSession = emptyWizardSession();

function emptyWizardSession() {
	return { step: 1, takipId: null, currentRecord: null, allowedActions: [], selectedAction: "", formValues: {} };
}

function escapeHtml(value) {
	return String(value ?? "")
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;");
}

function showToast(message, type = "success") {
	Toastify({
		text: message,
		duration: 3000,
		gravity: "top",
		position: "right",
		style: { background: type === "success" ? "#42525e" : "#ee4447" },
	}).showToast();
}

function todayDateOnly() {
	const now = new Date();
	const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
	return local.toISOString().slice(0, 10);
}

function formatDateTimeTr(value) {
	const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(value || ""));
	return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}` : "\u2014";
}

function formatPeriodType(value) {
	if (value === "ilk_faydali_omur") return "\u0130lk Faydal\u0131 \u00d6m\u00fcr";
	if (value === "operasyonel_kontrol") return "Operasyonel Kontrol";
	return "\u2014";
}

function addPeriodDateOnly(dateValue, yearsValue, monthsValue) {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateValue || ""));
	if (!match) return "";
	const clampDate = (year, month, day) => {
		const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
		return { year, month, day: Math.min(day, lastDay) };
	};
	const initialYear = Number(match[1]);
	const initialMonth = Number(match[2]) - 1;
	const initialDay = Number(match[3]);
	const afterYears = clampDate(initialYear + Number(yearsValue || 0), initialMonth, initialDay);
	const totalMonth = afterYears.month + Number(monthsValue || 0);
	const targetYear = afterYears.year + Math.floor(totalMonth / 12);
	const targetMonth = totalMonth % 12;
	const result = clampDate(targetYear, targetMonth, afterYears.day);
	return `${result.year}-${String(result.month + 1).padStart(2, "0")}-${String(result.day).padStart(2, "0")}`;
}

function addDaysDateOnly(dateValue, daysValue) {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateValue || ""));
	if (!match) return "";
	const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
	date.setUTCDate(date.getUTCDate() + Number(daysValue || 0));
	return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

function calculatePreviewStatus({ endDate, periodType, selectionValue, archived = false }) {
	if (archived) return "takip_arsivli";
	if (selectionValue === AMORTISMAN_UYGULANMAZ_VALUE) return "uygulanmaz";
	if (!endDate) return "belirlenmedi";
	const today = todayDateOnly();
	if (endDate <= today) {
		return periodType === "operasyonel_kontrol" ? "inceleme_bekliyor" : "faydali_omru_doldu";
	}
	return endDate <= addDaysDateOnly(today, 90) ? "yaklasiyor" : "devam_ediyor";
}

function ensureView() {
	if (document.getElementById("amortisman-takip-view")) return;
	document.getElementById("page-content")?.insertAdjacentHTML("beforeend", `
		<div id="amortisman-takip-view">
			<div class="page-section" id="amortisman-filter-section">
				<div class="section-header">
					<div class="section-header-left">
						<span class="material-icons section-icon">filter_list</span>
						<span class="section-title">Filtreler</span>
						<span class="filter-aktif-badge" hidden>
							<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
							Filtre aktif
						</span>
					</div>
					<div class="section-header-right"><button type="button" class="section-collapse-btn" title="Daralt/Genislet"><span class="material-icons">expand_less</span></button></div>
				</div>
				<div class="section-body">
					<div class="amortisman-filter-grid">
						<div class="column-container"><label>Arama</label><input id="amortisman-arama" type="text" placeholder="Takip, al\u0131m partisi, demirba\u015f, barkod veya fatura no" /></div>
						<div class="column-container"><label>Durum</label><select id="amortisman-durum"><option value="actionable">\u0130\u015flem Gerekenler</option><option value="all">T\u00fcm\u00fc</option>${STATUS_ORDER.map((status) => `<option value="${status}">${AMORTISMAN_STATUS_LABELS[status]}</option>`).join("")}</select></div>
						<div class="column-container"><label>Faydal\u0131 \u00d6m\u00fcr</label><select id="amortisman-tanim-filter"><option value="">T\u00fcm\u00fc</option></select></div>
						<div class="column-container"><label>Biti\u015f Ba\u015flang\u0131\u00e7</label><input id="amortisman-bitis-baslangic" type="date" /></div>
						<div class="column-container"><label>Biti\u015f Son</label><input id="amortisman-bitis-bitis" type="date" /></div>
						<div class="filter-button-container"><button type="button" class="amortisman-reset">Temizle</button><button type="button" class="amortisman-search">Ara</button></div>
					</div>
				</div>
			</div>

			<div class="page-section" id="amortisman-grid-section">
				<div class="section-header"><div class="section-header-left"><span class="section-title">Faydal\u0131 \u00d6m\u00fcr Takibi</span><span class="section-count" id="amortisman-count"></span></div></div>
				<div class="section-body"><div class="amortisman-grid"></div></div>
			</div>

			<div id="amortismanGridActionMenu" class="amortisman-grid-action-menu" hidden>
				<button type="button" data-action="overview"><span class="material-icons">visibility</span><span>G\u00f6r\u00fcnt\u00fcle</span></button>
				<button type="button" data-action="history"><span class="material-icons">history</span><span>Karar Ge\u00e7mi\u015fi</span></button>
				<button type="button" data-action="process"><span class="material-icons">task_alt</span><span>\u0130\u015flem Yap</span></button>
			</div>

			<dialog id="amortismanOverviewDialog" class="amortisman-overview-dialog">
				<div class="amortisman-overview-container">
					<header class="amortisman-overview-header">
						<div class="amortisman-overview-heading">
							<h2 class="amortisman-overview-title">Faydal\u0131 \u00d6m\u00fcr Takibi</h2>
							<div class="amortisman-overview-meta"></div>
							<div class="amortisman-overview-status"></div>
						</div>
						<div class="amortisman-overview-header-actions">
							<button type="button" class="amortisman-overview-close" aria-label="Kapat">&times;</button>
						</div>
					</header>
					<div class="amortisman-overview-body"></div>
					<footer class="amortisman-overview-footer"><button type="button" class="amortisman-overview-footer-close">Kapat</button></footer>
				</div>
			</dialog>

			<dialog id="amortismanWizardDialog" class="amortisman-dialog">
				<form class="amortisman-wizard-form">
					<div class="amortisman-dialog-header"><div><h3>\u0130\u015flem Yap</h3><div class="amortisman-dialog-context"></div></div><button type="button" class="amortisman-dialog-close" aria-label="Kapat">&#10005;</button></div>
					<div class="amortisman-wizard-steps"><span data-step="1">1. \u0130\u015flem Se\u00e7</span><span data-step="2">2. Bilgileri Gir</span><span data-step="3">3. Kontrol ve Onay</span></div>
					<div class="amortisman-wizard-body"></div>
					<div class="amortisman-dialog-error"></div>
					<div class="amortisman-dialog-actions"><button type="button" class="amortisman-dialog-cancel">\u0130ptal</button><button type="button" class="amortisman-wizard-back" hidden>Geri</button><button type="submit" class="amortisman-wizard-next">Devam</button></div>
				</form>
			</dialog>

			<dialog id="amortismanHistoryDialog" class="amortisman-dialog amortisman-history-dialog">
				<div class="amortisman-dialog-header"><div><h3>Karar Ge\u00e7mi\u015fi</h3><div class="amortisman-history-context"></div></div><button type="button" class="amortisman-dialog-close" aria-label="Kapat">&#10005;</button></div>
				<div class="amortisman-history-body"></div>
				<div class="amortisman-dialog-actions"><button type="button" class="amortisman-dialog-close">Tamam</button></div>
			</dialog>
		</div>`);
}

function fillDefinitionFilter() {
	const select = document.getElementById("amortisman-tanim-filter");
	if (!select) return;
	const current = uiState.amortisman.filters.tanim_id;
	select.innerHTML = '<option value="">T\u00fcm\u00fc</option>';
	definitions.forEach((definition) => {
		const option = document.createElement("option");
		option.value = String(definition.id);
		option.textContent = `${definition.name} - ${definition.faydali_omur_yil} y\u0131l${definition.is_active === false ? " (Pasif)" : ""}`;
		select.appendChild(option);
	});
	select.value = current;
}

function buildBaseUrl() {
	const params = new URLSearchParams();
	Object.entries(uiState.amortisman.filters).forEach(([key, value]) => { if (value) params.set(key, value); });
	return `${API}/amortisman-takip?${params.toString()}`;
}

function syncFilterBadge() {
	const filters = uiState.amortisman.filters;
	const active = Boolean(filters.arama || filters.tanim_id || filters.bitis_baslangic || filters.bitis_bitis || filters.durum !== "actionable");
	const badge = document.querySelector("#amortisman-filter-section .filter-aktif-badge");
	if (badge) badge.hidden = !active;
}

function statusBadgeMarkup(status) {
	return `<span class="amortisman-status-badge amortisman-status-badge--${escapeHtml(status)}">${escapeHtml(AMORTISMAN_STATUS_LABELS[status] || status)}</span>`;
}

function statusBadge(status) {
	return html(statusBadgeMarkup(status));
}

function formatRemainingDays(value) {
	if (value == null) return "\u2014";
	const days = Number(value);
	return days >= 0 ? `${days} g\u00fcn kald\u0131` : `${Math.abs(days)} g\u00fcn ge\u00e7ti`;
}

function trackingIdFrom(source) {
	return Number(typeof source === "number" ? source : source?.dataset?.takipId);
}

function overviewDetail(label, value, wide = false) {
	return `<div class="amortisman-overview-detail${wide ? " is-wide" : ""}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value ?? "\u2014")}</strong></div>`;
}

function closeGridActionMenu() {
	const menu = document.getElementById("amortismanGridActionMenu");
	if (!menu) return;
	menu.hidden = true;
	delete menu.dataset.takipId;
	document.querySelectorAll(".amortisman-grid-action-toggle[aria-expanded='true']").forEach((button) => {
		button.setAttribute("aria-expanded", "false");
	});
}

function toggleGridActionMenu(button) {
	const menu = document.getElementById("amortismanGridActionMenu");
	if (!menu) return;
	const takipId = trackingIdFrom(button);
	const sameOpenMenu = !menu.hidden && Number(menu.dataset.takipId) === takipId;
	closeGridActionMenu();
	if (sameOpenMenu) return;

	menu.dataset.takipId = String(takipId);
	menu.hidden = false;
	button.setAttribute("aria-expanded", "true");
	const buttonRect = button.getBoundingClientRect();
	const menuRect = menu.getBoundingClientRect();
	const margin = 8;
	const left = Math.min(
		Math.max(margin, buttonRect.right - menuRect.width),
		window.innerWidth - menuRect.width - margin,
	);
	const opensUpward = buttonRect.bottom + menuRect.height + margin > window.innerHeight;
	const top = opensUpward
		? Math.max(margin, buttonRect.top - menuRect.height - 6)
		: buttonRect.bottom + 6;
	menu.style.left = `${left}px`;
	menu.style.top = `${top}px`;
}

function renderOverviewLoading() {
	const body = document.querySelector("#amortismanOverviewDialog .amortisman-overview-body");
	if (body) body.innerHTML = '<div class="amortisman-dialog-loading"><span class="amortisman-spinner"></span><span>G\u00fcncel takip bilgisi y\u00fckleniyor...</span></div>';
}

function renderOverview(record) {
	const dialog = document.getElementById("amortismanOverviewDialog");
	if (!dialog) return;
	const product = [record.urun_cinsi, record.marka, record.model].filter(Boolean).join(" / ") || "Demirba\u015f";
	const definition = record.amortisman_uygulanmaz ? "Amortisman uygulanmaz" : record.amortisman_tanim_adi || "Belirlenmedi";
	const duration = record.faydali_omur_yil ? `${record.faydali_omur_yil} y\u0131l` : "\u2014";
	dialog.dataset.takipId = String(record.takip_id);
	dialog.querySelector(".amortisman-overview-title").textContent = product;
	dialog.querySelector(".amortisman-overview-meta").innerHTML = `<span>Takip #${escapeHtml(record.takip_id)}</span><span>Al\u0131m Partisi #${escapeHtml(record.fatura_id)}</span><span>${escapeHtml(record.demirbas_id)}</span>`;
	dialog.querySelector(".amortisman-overview-status").innerHTML = statusBadgeMarkup(record.durum);
	dialog.querySelector(".amortisman-overview-body").innerHTML = `
		<section class="amortisman-overview-section">
			<header><span class="material-icons">receipt_long</span><h3>Takip ve Al\u0131m Partisi</h3></header>
			<div class="amortisman-overview-detail-grid">
				${overviewDetail("Faydal\u0131 \u00d6m\u00fcr Takip ID", record.takip_id)}
				${overviewDetail("Al\u0131m Partisi ID", record.fatura_id)}
				${overviewDetail("Demirba\u015f ID", record.demirbas_id)}
				${overviewDetail("Barkod", record.barkod)}
				${overviewDetail("Fatura Numaras\u0131", record.fatura_numarasi)}
				${overviewDetail("Temsil Adedi", record.temsil_adedi)}
				${overviewDetail("Sat\u0131n Alma Tarihi", formatDateOnlyTr(record.satin_alma_tarihi))}
			</div>
		</section>
		<section class="amortisman-overview-section">
			<header><span class="material-icons">schedule</span><h3>Faydal\u0131 \u00d6m\u00fcr D\u00f6nemi</h3></header>
			<div class="amortisman-overview-detail-grid">
				${overviewDetail("Faydal\u0131 \u00d6m\u00fcr Tan\u0131m\u0131", definition, true)}
				${overviewDetail("Tan\u0131m S\u00fcresi", duration)}
				${overviewDetail("D\u00f6nem T\u00fcr\u00fc", formatPeriodType(record.donem_tipi))}
				${overviewDetail("D\u00f6nem Ba\u015flang\u0131c\u0131", formatDateOnlyTr(record.baslangic_tarihi))}
				${overviewDetail("D\u00f6nem Biti\u015fi", formatDateOnlyTr(record.bitis_tarihi))}
				${overviewDetail("Kalan / Ge\u00e7en", formatRemainingDays(record.kalan_gun))}
				${overviewDetail("G\u00fcncel Durum", AMORTISMAN_STATUS_LABELS[record.durum] || record.durum)}
			</div>
		</section>`;
}

async function openOverview(source) {
	const dialog = document.getElementById("amortismanOverviewDialog");
	const takipId = trackingIdFrom(source);
	if (!dialog || !takipId) return;
	overviewController?.abort();
	overviewController = new AbortController();
	const requestId = ++overviewRequestId;
	dialog.dataset.takipId = String(takipId);
	if (!dialog.open) dialog.showModal();
	renderOverviewLoading();
	try {
		const data = await fetchJson(`${API}/faydali-omur-takipleri/${takipId}`, { signal: overviewController.signal });
		if (requestId !== overviewRequestId || !dialog.open) return;
		renderOverview(data.item);
	} catch (error) {
		if (error.name !== "AbortError" && requestId === overviewRequestId && dialog.open) {
			dialog.querySelector(".amortisman-overview-body").innerHTML = `<div class="amortisman-history-empty is-error">${escapeHtml(error.message)}</div>`;
		}
	}
}

function buildSortUrl(previousUrl, columns) {
	const url = new URL(previousUrl, window.location.origin);
	url.searchParams.delete("sort");
	url.searchParams.delete("order");
	const column = columns?.[0];
	const field = column ? TRACKING_SORT_FIELDS[column.index] : null;
	if (field) {
		url.searchParams.set("sort", field);
		url.searchParams.set("order", column.direction === -1 ? "desc" : "asc");
	}
	return `${url.pathname}${url.search}`;
}

function renderGrid() {
	const target = document.querySelector(".amortisman-grid");
	if (!target) return;
	closeGridActionMenu();
	trackingGrid?.destroy();
	target.innerHTML = "";
	trackingGrid = new Grid({
		columns: [
			{ name: "Takip ID", width: "82px" }, { name: "Demirba\u015f ID", width: "120px" },
			{ name: "Barkod", width: "95px" }, { name: "\u00dcr\u00fcn / Marka / Model", width: "240px" },
			{ name: "Fatura No", width: "155px" }, { name: "Temsil Adedi", width: "90px" },
			{ name: "D\u00f6nem Biti\u015fi", width: "110px" }, { name: "Kalan / Ge\u00e7en", width: "110px" },
			{ name: "Durum", width: "135px", formatter: (cell) => statusBadge(cell) },
			{ name: "\u0130\u015flem", width: "64px", sort: false, formatter: (cell) => html(cell) },
		],
		server: {
			url: buildBaseUrl(),
			total: (data) => Number(data.total || 0),
			then: (data) => {
				const count = document.getElementById("amortisman-count");
				if (count) count.textContent = `${data.total || 0} kay\u0131t`;
				return (data.items || []).map((item) => {
					const attrs = `data-takip-id="${item.takip_id}"`;
					return [
						item.takip_id, item.demirbas_id, item.barkod,
						[item.urun_cinsi, item.marka, item.model].filter(Boolean).join(" / "),
						item.fatura_numarasi || "\u2014", item.temsil_adedi, formatDateOnlyTr(item.bitis_tarihi),
						formatRemainingDays(item.kalan_gun), item.durum,
						`<div class="grid-actions"><button type="button" class="grid-action-btn amortisman-grid-action-toggle" ${attrs} title="\u0130\u015flemler" aria-label="\u0130\u015flemler" aria-expanded="false"><span class="material-icons">more_horiz</span></button></div>`,
					];
				});
			},
		},
		pagination: {
			limit: uiState.amortisman.paginationLimit,
			server: { url: (url, page, limit) => `${url}${url.includes("?") ? "&" : "?"}limit=${limit}&offset=${page * limit}` },
		},
		search: false,
		sort: { multiColumn: false, server: { url: buildSortUrl } },
		style: { table: { "white-space": "nowrap", "font-size": "11px" }, td: { padding: "10px 12px" }, th: { padding: "8px 12px" } },
	}).render(target);
	syncFilterBadge();
}

function syncFiltersFromDom() {
	uiState.amortisman.filters = {
		arama: document.getElementById("amortisman-arama")?.value.trim() || "",
		durum: document.getElementById("amortisman-durum")?.value || "actionable",
		tanim_id: document.getElementById("amortisman-tanim-filter")?.value || "",
		bitis_baslangic: document.getElementById("amortisman-bitis-baslangic")?.value || "",
		bitis_bitis: document.getElementById("amortisman-bitis-bitis")?.value || "",
	};
}

async function fetchJson(url, options = {}) {
	const response = await fetch(url, options);
	const data = await response.json().catch(() => ({}));
	if (!response.ok) throw new Error(data?.error || "Islem tamamlanamadi.");
	return data;
}

function trackingContext(record) {
	return `${record.demirbas_id} \u00b7 Al\u0131m Partisi #${record.fatura_id} \u00b7 Takip #${record.takip_id}`;
}

function setWizardError(message = "") {
	const error = document.querySelector("#amortismanWizardDialog .amortisman-dialog-error");
	if (error) error.textContent = message;
}

function currentSelectionValue(record) {
	if (record?.amortisman_uygulanmaz) return AMORTISMAN_UYGULANMAZ_VALUE;
	return record?.amortisman_tanim_id ? String(record.amortisman_tanim_id) : "";
}

function renderWizardStepOne(body) {
	body.innerHTML = `<div class="amortisman-action-list">${wizardSession.allowedActions.map((action) => {
		const config = ACTION_CONFIG[action];
		if (!config) return "";
		return `<label class="amortisman-action-card${config.danger ? " is-danger" : ""}"><input type="radio" name="amortisman-wizard-action" value="${action}"${wizardSession.selectedAction === action ? " checked" : ""} /><span><strong>${config.label}</strong><small>${config.description}</small></span></label>`;
	}).join("")}</div>`;
}

function wizardDateField(value) {
	return `<div class="column-container"><label>Karar Tarihi</label><input class="amortisman-wizard-date" type="date" max="${todayDateOnly()}" value="${escapeHtml(value || todayDateOnly())}" required /></div>`;
}

function wizardDurationFields(values, revision = false) {
	return `<div class="amortisman-wizard-duration-block"><label>${revision ? "Yeni Toplam D\u00f6nem S\u00fcresi" : "Yeni Kontrol S\u00fcresi"}</label><div class="amortisman-duration-fields"><div class="column-container"><label>Y\u0131l</label><input class="amortisman-duration-year" type="number" min="0" max="100" step="1" value="${escapeHtml(values.sure_yil ?? "0")}" required /></div><div class="column-container"><label>Ay</label><input class="amortisman-duration-month" type="number" min="0" max="11" step="1" value="${escapeHtml(values.sure_ay ?? "1")}" required /></div></div><small>S\u00fcre en az 1 ay, en fazla 100 y\u0131l olmal\u0131d\u0131r.</small></div>`;
}

function wizardNoteField(value, required = true) {
	return `<div class="column-container"><label>Karar Notu${required ? "" : " (Opsiyonel)"}</label><textarea class="amortisman-wizard-note" maxlength="1000" ${required ? "required" : ""} placeholder="Karar\u0131n gerek\u00e7esini yaz\u0131n...">${escapeHtml(value || "")}</textarea></div>`;
}

function renderWizardStepTwo(body) {
	const action = wizardSession.selectedAction;
	const values = wizardSession.formValues;
	const record = wizardSession.currentRecord;
	if (["faydali_omur_tanimla", "secimi_duzelt"].includes(action)) {
		body.innerHTML = `<div class="amortisman-current-summary">Mevcut se\u00e7im: <strong>${escapeHtml(record.amortisman_uygulanmaz ? "Uygulanmaz" : record.amortisman_tanim_adi || "Belirlenmedi")}</strong></div><div class="column-container"><label>Faydal\u0131 \u00d6m\u00fcr</label><select class="amortisman-wizard-definition form-select" required></select></div>${wizardDateField(values.karar_tarihi)}${wizardNoteField(values.note, action === "secimi_duzelt")}`;
		const select = body.querySelector(".amortisman-wizard-definition");
		const selectedValue = values.selection_value ?? currentSelectionValue(record);
		const currentDefinition = record.amortisman_tanim_id ? {
			id: record.amortisman_tanim_id,
			name: record.amortisman_tanim_adi,
			faydali_omur_yil: record.faydali_omur_yil,
			is_active: Boolean(record.amortisman_tanim_aktif),
		} : null;
		populateAmortismanSelect(select, definitions.filter((item) => item.is_active !== false), {
			selectedDefinitionId: selectedValue === AMORTISMAN_UYGULANMAZ_VALUE ? "" : selectedValue,
			uygulanmaz: selectedValue === AMORTISMAN_UYGULANMAZ_VALUE,
			currentDefinition,
		});
		return;
	}
	if (action === "inceleme_notu") {
		body.innerHTML = `${wizardDateField(values.karar_tarihi)}${wizardNoteField(values.note)}`;
		return;
	}
	if (["kullanima_devam", "bakim_tamir_sonrasi_devam", "sure_revizyonu"].includes(action)) {
		body.innerHTML = `${wizardDateField(values.karar_tarihi)}${wizardDurationFields(values, action === "sure_revizyonu")}${wizardNoteField(values.note)}`;
		return;
	}
	if (action === "takibi_arsivle") {
		body.innerHTML = `<div class="amortisman-warning"><strong>Bu i\u015flem geri al\u0131namaz.</strong><br />Takip aktif listeden kalkar. Al\u0131m partisi ve ge\u00e7mi\u015f, Demirba\u015f ekran\u0131ndan ayr\u0131ca incelenebilir.</div>${wizardNoteField(values.note)}`;
	}
}

function selectedDefinitionText(values) {
	if (values.selection_value === AMORTISMAN_UYGULANMAZ_VALUE) return "Amortisman uygulanmaz";
	const definition = definitions.find((item) => String(item.id) === String(values.selection_value));
	return definition ? `${definition.name} - ${definition.faydali_omur_yil} y\u0131l` : "\u2014";
}

function buildWizardPreview() {
	const action = wizardSession.selectedAction;
	const values = wizardSession.formValues;
	const record = wizardSession.currentRecord;
	const rows = [["\u0130\u015flem", ACTION_CONFIG[action]?.label || action], ["Mevcut Durum", AMORTISMAN_STATUS_LABELS[record.durum] || record.durum]];
	let resultingStatus = record.durum;
	if (values.karar_tarihi) rows.push(["Karar Tarihi", formatDateOnlyTr(values.karar_tarihi)]);
	if (["faydali_omur_tanimla", "secimi_duzelt"].includes(action)) {
		rows.push(["Yeni Se\u00e7im", selectedDefinitionText(values)]);
		const definition = definitions.find((item) => String(item.id) === String(values.selection_value));
		const endDate = definition ? addYearsDateOnly(record.satin_alma_tarihi, definition.faydali_omur_yil) : "";
		rows.push(["Yeni D\u00f6nem", endDate ? `${formatDateOnlyTr(record.satin_alma_tarihi)} \u2192 ${formatDateOnlyTr(endDate)}` : "D\u00f6nem olu\u015fturulmayacak"]);
		resultingStatus = calculatePreviewStatus({ endDate, periodType: "ilk_faydali_omur", selectionValue: values.selection_value });
	}
	if (["kullanima_devam", "bakim_tamir_sonrasi_devam"].includes(action)) {
		const startCandidates = [record.bitis_tarihi, values.karar_tarihi].filter(Boolean).sort();
		const start = startCandidates[startCandidates.length - 1] || "";
		const endDate = addPeriodDateOnly(start, values.sure_yil, values.sure_ay);
		rows.push(["Yeni Kontrol D\u00f6nemi", `${formatDateOnlyTr(start)} \u2192 ${formatDateOnlyTr(endDate)}`]);
		resultingStatus = calculatePreviewStatus({ endDate, periodType: "operasyonel_kontrol" });
	}
	if (action === "sure_revizyonu") {
		const endDate = addPeriodDateOnly(record.baslangic_tarihi, values.sure_yil, values.sure_ay);
		rows.push(["Mevcut Biti\u015f", formatDateOnlyTr(record.bitis_tarihi)]);
		rows.push(["Yeni Biti\u015f", formatDateOnlyTr(endDate)]);
		resultingStatus = calculatePreviewStatus({ endDate, periodType: record.donem_tipi });
	}
	if (action === "takibi_arsivle") resultingStatus = "takip_arsivli";
	rows.push(["Olu\u015facak Durum", AMORTISMAN_STATUS_LABELS[resultingStatus] || resultingStatus]);
	if (values.note) rows.push(["Karar Notu", values.note]);
	return rows;
}

function renderWizardStepThree(body) {
	body.innerHTML = `<div class="amortisman-review-card">${buildWizardPreview().map(([label, value]) => `<div><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`).join("")}</div>`;
}

function renderWizard() {
	const dialog = document.getElementById("amortismanWizardDialog");
	const body = dialog?.querySelector(".amortisman-wizard-body");
	if (!dialog || !body || !wizardSession.currentRecord) return;
	dialog.querySelector(".amortisman-dialog-context").textContent = trackingContext(wizardSession.currentRecord);
	dialog.querySelectorAll(".amortisman-wizard-steps span").forEach((step) => {
		step.classList.toggle("is-active", Number(step.dataset.step) === wizardSession.step);
		step.classList.toggle("is-complete", Number(step.dataset.step) < wizardSession.step);
	});
	if (wizardSession.step === 1) renderWizardStepOne(body);
	if (wizardSession.step === 2) renderWizardStepTwo(body);
	if (wizardSession.step === 3) renderWizardStepThree(body);
	dialog.querySelector(".amortisman-wizard-back").hidden = wizardSession.step === 1;
	const next = dialog.querySelector(".amortisman-wizard-next");
	next.textContent = wizardSession.step === 3 ? (wizardSession.selectedAction === "takibi_arsivle" ? "Takibi Ar\u015fivle" : "Kaydet") : "Devam";
	next.classList.toggle("is-danger", wizardSession.step === 3 && wizardSession.selectedAction === "takibi_arsivle");
	setWizardError();
}

function renderWizardLoading() {
	const body = document.querySelector("#amortismanWizardDialog .amortisman-wizard-body");
	if (body) body.innerHTML = '<div class="amortisman-dialog-loading"><span class="amortisman-spinner"></span><span>G\u00fcncel takip bilgisi y\u00fckleniyor...</span></div>';
}

async function openWizard(source) {
	const dialog = document.getElementById("amortismanWizardDialog");
	const takipId = trackingIdFrom(source);
	if (!dialog || !takipId || wizardSubmitting) return;
	wizardController?.abort();
	wizardController = new AbortController();
	const requestId = ++wizardRequestId;
	wizardSession = { ...emptyWizardSession(), takipId };
	dialog.querySelector(".amortisman-dialog-context").textContent = `Takip #${takipId}`;
	if (!dialog.open) dialog.showModal();
	renderWizardLoading();
	setWizardError();
	try {
		const data = await fetchJson(`${API}/faydali-omur-takipleri/${takipId}`, { signal: wizardController.signal });
		if (requestId !== wizardRequestId || !dialog.open) return;
		wizardSession.currentRecord = data.item;
		wizardSession.allowedActions = data.allowed_actions || [];
		renderWizard();
	} catch (error) {
		if (error.name !== "AbortError" && requestId === wizardRequestId && dialog.open) {
			dialog.querySelector(".amortisman-wizard-body").innerHTML = `<div class="amortisman-history-empty is-error">${escapeHtml(error.message)}</div>`;
			setWizardError(error.message);
		}
	}
}

function captureWizardValues({ validate = true } = {}) {
	const dialog = document.getElementById("amortismanWizardDialog");
	const values = { ...wizardSession.formValues };
	const action = wizardSession.selectedAction;
	const fields = {
		date: dialog.querySelector(".amortisman-wizard-date"),
		note: dialog.querySelector(".amortisman-wizard-note"),
		definition: dialog.querySelector(".amortisman-wizard-definition"),
		years: dialog.querySelector(".amortisman-duration-year"),
		months: dialog.querySelector(".amortisman-duration-month"),
	};
	if (fields.date) values.karar_tarihi = fields.date.value;
	if (fields.note) values.note = fields.note.value.trim();
	if (fields.definition) values.selection_value = fields.definition.value;
	if (fields.years) values.sure_yil = fields.years.value;
	if (fields.months) values.sure_ay = fields.months.value;
	if (validate && Object.values(fields).some((field) => field && !field.reportValidity())) return null;
	if (validate && (fields.years || fields.months)) {
		const years = Number(values.sure_yil);
		const months = Number(values.sure_ay);
		const totalMonths = years * 12 + months;
		if (!Number.isInteger(years) || !Number.isInteger(months) || months > 11 || totalMonths < 1 || totalMonths > 1200) {
			setWizardError("S\u00fcre en az 1 ay, en fazla 100 y\u0131l olmal\u0131d\u0131r.");
			return null;
		}
	}
	if (validate && action === "secimi_duzelt" && values.selection_value === currentSelectionValue(wizardSession.currentRecord)) {
		setWizardError("Yeni faydal\u0131 \u00f6m\u00fcr se\u00e7imi mevcut se\u00e7imden farkl\u0131 olmal\u0131d\u0131r.");
		return null;
	}
	wizardSession.formValues = values;
	return values;
}

function selectionPayload(value) {
	return value === AMORTISMAN_UYGULANMAZ_VALUE
		? { amortisman_tanim_id: null, amortisman_uygulanmaz: true }
		: { amortisman_tanim_id: Number(value), amortisman_uygulanmaz: false };
}

async function submitWizard() {
	const dialog = document.getElementById("amortismanWizardDialog");
	const button = dialog.querySelector(".amortisman-wizard-next");
	const action = wizardSession.selectedAction;
	const values = wizardSession.formValues;
	const record = wizardSession.currentRecord;
	wizardSubmitting = true;
	dialog.querySelectorAll("button").forEach((control) => { control.disabled = true; });
	try {
		if (["faydali_omur_tanimla", "secimi_duzelt"].includes(action)) {
			await fetchJson(`${API}/faturalar/${record.fatura_id}/amortisman`, {
				method: "PATCH", headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ ...selectionPayload(values.selection_value), karar_tarihi: values.karar_tarihi, note: values.note || null }),
			});
		} else if (action === "takibi_arsivle") {
			await fetchJson(`${API}/faydali-omur-takipleri/${record.takip_id}/arsiv`, {
				method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note: values.note }),
			});
		} else {
			await fetchJson(`${API}/faydali-omur-takipleri/${record.takip_id}/hareketler`, {
				method: "POST", headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ hareket_tipi: action, karar_tarihi: values.karar_tarihi, sure_yil: values.sure_yil ?? 0, sure_ay: values.sure_ay ?? 0, note: values.note }),
			});
		}
		wizardSubmitting = false;
		dialog.close();
		showToast(action === "takibi_arsivle" ? "Faydal\u0131 \u00f6m\u00fcr takibi ar\u015fivlendi." : "Faydal\u0131 \u00f6m\u00fcr i\u015flemi kaydedildi.");
		renderGrid();
		const overview = document.getElementById("amortismanOverviewDialog");
		if (overview?.open && Number(overview.dataset.takipId) === Number(record.takip_id)) {
			if (action === "takibi_arsivle") overview.close();
			else openOverview(record.takip_id);
		}
	} catch (error) {
		setWizardError(error.message);
	} finally {
		wizardSubmitting = false;
		dialog.querySelectorAll("button").forEach((control) => { control.disabled = false; });
		button.disabled = false;
	}
}

function renderHistoryItems(items) {
	if (!items.length) return '<div class="amortisman-history-empty">Bu takip i\u00e7in kay\u0131tl\u0131 hareket bulunamad\u0131.</div>';
	return items.map((item) => {
		const duration = Number(item.sure_yil || 0) || Number(item.sure_ay || 0) ? `${Number(item.sure_yil || 0)} y\u0131l ${Number(item.sure_ay || 0)} ay` : null;
		const oldPeriod = item.onceki_baslangic_tarihi || item.onceki_bitis_tarihi ? `${formatDateOnlyTr(item.onceki_baslangic_tarihi)} \u2192 ${formatDateOnlyTr(item.onceki_bitis_tarihi)}` : null;
		const newPeriod = item.yeni_baslangic_tarihi || item.yeni_bitis_tarihi ? `${formatDateOnlyTr(item.yeni_baslangic_tarihi)} \u2192 ${formatDateOnlyTr(item.yeni_bitis_tarihi)}` : null;
		const details = [
			["Karar Tarihi", formatDateOnlyTr(item.karar_tarihi)], ["Kay\u0131t Tarihi", formatDateTimeTr(item.created_at)],
			["Yapan", item.yapan_adi_snapshot || "Sistem"], duration ? ["S\u00fcre", duration] : null,
			oldPeriod ? ["\u00d6nceki D\u00f6nem", oldPeriod] : null, newPeriod ? ["Yeni D\u00f6nem", newPeriod] : null,
			["Temsil Snapshot", item.temsil_adedi_snapshot],
		].filter(Boolean);
		return `<article class="amortisman-history-card"><header><div><span class="amortisman-history-id">Hareket #${item.id}</span><h4>${escapeHtml(MOVEMENT_LABELS[item.hareket_tipi] || item.hareket_tipi)}</h4></div><time>${escapeHtml(formatDateOnlyTr(item.karar_tarihi))}</time></header><div class="amortisman-history-details">${details.map(([label, value]) => `<div><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`).join("")}</div>${item.note ? `<div class="amortisman-history-note"><span>Not</span><p>${escapeHtml(item.note)}</p></div>` : ""}</article>`;
	}).join("");
}

async function openHistory(source) {
	const dialog = document.getElementById("amortismanHistoryDialog");
	const takipId = trackingIdFrom(source);
	if (!dialog || !takipId) return;
	historyController?.abort();
	historyController = new AbortController();
	const requestId = ++historyRequestId;
	dialog.querySelector(".amortisman-history-context").textContent = `Takip #${takipId}`;
	dialog.querySelector(".amortisman-history-body").innerHTML = '<div class="amortisman-dialog-loading"><span class="amortisman-spinner"></span><span>Karar ge\u00e7mi\u015fi y\u00fckleniyor...</span></div>';
	if (!dialog.open) dialog.showModal();
	try {
		const data = await fetchJson(`${API}/faydali-omur-takipleri/${takipId}/hareketler`, { signal: historyController.signal });
		if (requestId !== historyRequestId || !dialog.open) return;
		dialog.querySelector(".amortisman-history-context").textContent = trackingContext(data.tracking);
		dialog.querySelector(".amortisman-history-body").innerHTML = renderHistoryItems(data.items || []);
	} catch (error) {
		if (error.name !== "AbortError" && requestId === historyRequestId) dialog.querySelector(".amortisman-history-body").innerHTML = `<div class="amortisman-history-empty is-error">${escapeHtml(error.message)}</div>`;
	}
}

function closeDialog(dialog) {
	if (dialog?.open) dialog.close();
}

function bindEvents() {
	if (didBind) return;
	didBind = true;
	document.body.addEventListener("click", (event) => {
		if (event.target.closest(".amortisman-search")) { syncFiltersFromDom(); renderGrid(); return; }
		if (event.target.closest(".amortisman-reset")) {
			uiState.amortisman.filters = { arama: "", durum: "actionable", tanim_id: "", bitis_baslangic: "", bitis_bitis: "" };
			renderAmortismanPage();
			return;
		}
		const collapse = event.target.closest("#amortisman-filter-section .section-collapse-btn");
		if (collapse) { collapse.closest(".page-section")?.classList.toggle("collapsed"); return; }
		const actionToggle = event.target.closest(".amortisman-grid-action-toggle");
		if (actionToggle) {
			toggleGridActionMenu(actionToggle);
			return;
		}
		const actionItem = event.target.closest("#amortismanGridActionMenu [data-action]");
		if (actionItem) {
			const menu = document.getElementById("amortismanGridActionMenu");
			const takipId = trackingIdFrom(menu);
			const action = actionItem.dataset.action;
			closeGridActionMenu();
			if (action === "overview") openOverview(takipId);
			if (action === "history") openHistory(takipId);
			if (action === "process") openWizard(takipId);
			return;
		}
		if (event.target.closest(".amortisman-overview-close, .amortisman-overview-footer-close")) {
			closeDialog(document.getElementById("amortismanOverviewDialog"));
			return;
		}
		const history = event.target.closest(".amortisman-history-btn");
		if (history) { openHistory(history); return; }
		const process = event.target.closest(".amortisman-process-btn");
		if (process) { openWizard(process); return; }
		const close = event.target.closest(".amortisman-dialog-close, .amortisman-dialog-cancel");
		if (close) {
			const dialog = close.closest("dialog");
			if (dialog?.id === "amortismanWizardDialog" && wizardSubmitting) return;
			closeDialog(dialog);
			return;
		}
		if (event.target.closest(".amortisman-wizard-back")) {
			if (wizardSession.step === 2) { captureWizardValues({ validate: false }); wizardSession.step = 1; }
			else if (wizardSession.step === 3) wizardSession.step = 2;
			renderWizard();
			return;
		}
		if (!event.target.closest("#amortismanGridActionMenu")) closeGridActionMenu();
	});

	document.body.addEventListener("change", (event) => {
		if (event.target.matches('input[name="amortisman-wizard-action"]')) {
			wizardSession.selectedAction = event.target.value;
			wizardSession.formValues = {};
		}
	});

	document.body.addEventListener("keydown", (event) => {
		if (event.key === "Enter" && event.target.matches("#amortisman-arama")) {
			event.preventDefault();
			syncFiltersFromDom();
			renderGrid();
		}
	});

	document.body.addEventListener("submit", async (event) => {
		if (!event.target.matches(".amortisman-wizard-form")) return;
		event.preventDefault();
		if (wizardSession.step === 1) {
			if (!wizardSession.selectedAction) { setWizardError("Devam etmek i\u00e7in bir i\u015flem se\u00e7iniz."); return; }
			wizardSession.step = 2;
			renderWizard();
			return;
		}
		if (wizardSession.step === 2) {
			if (!captureWizardValues()) return;
			wizardSession.step = 3;
			renderWizard();
			return;
		}
		await submitWizard();
	});

	document.getElementById("amortismanWizardDialog")?.addEventListener("close", () => {
		wizardController?.abort();
		wizardController = null;
		wizardRequestId += 1;
		wizardSession = emptyWizardSession();
	});
	document.getElementById("amortismanWizardDialog")?.addEventListener("cancel", (event) => {
		if (wizardSubmitting) event.preventDefault();
	});
	document.getElementById("amortismanHistoryDialog")?.addEventListener("close", () => {
		historyController?.abort();
		historyController = null;
		historyRequestId += 1;
	});
	document.getElementById("amortismanOverviewDialog")?.addEventListener("close", () => {
		overviewController?.abort();
		overviewController = null;
		overviewRequestId += 1;
		const dialog = document.getElementById("amortismanOverviewDialog");
		if (dialog) delete dialog.dataset.takipId;
	});
	window.addEventListener("resize", closeGridActionMenu);
	window.addEventListener("scroll", closeGridActionMenu, true);
}

export async function renderAmortismanPage() {
	ensureView();
	bindEvents();
	const filters = uiState.amortisman.filters;
	if (!FILTER_STATUSES.has(filters.durum)) filters.durum = "actionable";
	document.getElementById("amortisman-arama").value = filters.arama;
	document.getElementById("amortisman-durum").value = filters.durum;
	document.getElementById("amortisman-bitis-baslangic").value = filters.bitis_baslangic;
	document.getElementById("amortisman-bitis-bitis").value = filters.bitis_bitis;
	try {
		definitions = await fetchAmortismanDefinitions({ includeInactive: true });
		fillDefinitionFilter();
	} catch (error) {
		showToast(error.message, "error");
	}
	syncFilterBadge();
	renderGrid();
}

export function resetAmortismanPage() {
	trackingGrid?.destroy();
	trackingGrid = null;
	wizardController?.abort();
	overviewController?.abort();
	historyController?.abort();
	closeGridActionMenu();
	closeDialog(document.getElementById("amortismanWizardDialog"));
	closeDialog(document.getElementById("amortismanOverviewDialog"));
	closeDialog(document.getElementById("amortismanHistoryDialog"));
}
